import { useId, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Chip } from "@/components/chip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  type RoadmapItem,
  type RoadmapTriageEntry,
  useAcceptRoadmapTriage,
  useCreateRoadmapItem,
  useProposeRoadmapIdea,
} from "@/lib/api";
import { apiErrorMessage, inlineFailure } from "@/lib/api-errors";
import { formatDateTime } from "@/lib/format";
import { isLanguage } from "@/lib/i18n/language";
import type { NamedPerson } from "@/lib/people";

import { EffortStepper } from "./effort-stepper";
import {
  Field,
  LanguageSelect,
  OptionalPrioritySelect,
  PrioritySelect,
  TEXTAREA_CLASS,
} from "./item-form";
import { StepButtons } from "./step-buttons";
import { returnTriageFocus, submitterLabel, triageDraft, triageKind } from "./triage";
import {
  ideaCreate,
  type IdeaDraft,
  ideaProposal,
  linearProblem,
  linearProblemMessage,
  type ProposalDraft,
  SUMMARY_MAX,
  TITLE_MAX,
} from "./utils";

/** Which form the dialog shows. */
type IdeaMode = "create" | "accept" | "propose";

interface NewIdeaDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** A developer adds the idea to the roadmap; anyone else proposes it to the triage. */
  developer: boolean;
  /**
   * A triage entry to put on the roadmap (Naar backlog, a developer's): the
   * form starts from it. Null for a new idea from scratch.
   */
  entry?: RoadmapTriageEntry | null;
  /**
   * The entry has left the triage since the page loaded (`triageEntryGone`):
   * another developer, or Claude, handled it. The form says so and saves nothing.
   */
  entryGone?: boolean;
  /** The people the triage names its submitters among (`triagePeople`), by email in lower case. */
  people?: ReadonlyMap<string, NamedPerson>;
  /** After a developer added the idea: the item as the server returned it. */
  onCreated?: (item: RoadmapItem) => void;
  /** After someone proposed an idea: the page thanks them. */
  onProposed?: () => void;
}

/** Nobody to name a submitter among, while the roadmap loads. */
const NO_PEOPLE: ReadonlyMap<string, NamedPerson> = new Map();

/**
 * "Nieuw idee", by role. A developer adds an idea straight to Ideeën: a
 * title, a summary, a priority, the Linear tickets behind it and, when known,
 * an effort; whoever adds it follows it. The same form puts a triage entry on
 * the roadmap (Naar backlog), which then leaves the triage: an idea starts
 * from its own texts, a question from empty boxes under the question, to be
 * written in the developer's own words (`triageDraft`). Anyone else proposes
 * an idea (Idee voorstellen): a title, a summary and, if they like, a
 * priority. It goes to the triage, which only a developer sees, so it never
 * shows in their own list; the page thanks them.
 *
 * A new idea, and a proposed one, is stored in the dashboard's language at
 * that moment, the one it was written in. Naar backlog shows the language as
 * a choice, starting from an idea's own (a question's texts are new, so on
 * the dashboard's), since the developer may write in another. Should the
 * entry leave the triage while the dialog is open (it was handled elsewhere,
 * most likely from Claude), the form says so and Naar backlog stays off, so
 * nothing more is typed in vain. The dialog has no trigger of its own: it
 * gives the focus back to
 * what opened it, or, for an entry whose row's menu or row is gone, to the
 * row's menu or the group's toggle (`returnTriageFocus`).
 */
export function NewIdeaDialog({
  open,
  onOpenChange,
  developer,
  entry = null,
  entryGone = false,
  people = NO_PEOPLE,
  onCreated,
  onProposed,
}: NewIdeaDialogProps) {
  const { t } = useTranslation();
  const opener = useRef<HTMLElement | null>(null);
  // What had the focus as it opened, before the title box takes it: read before the dialog mounts.
  useLayoutEffect(() => {
    if (open) {
      const active = document.activeElement;
      opener.current = active instanceof HTMLElement && active !== document.body ? active : null;
    }
  }, [open]);
  const mode: IdeaMode = entry ? "accept" : developer ? "create" : "propose";
  const close = () => onOpenChange(false);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg"
        onCloseAutoFocus={(event) => {
          if (entry) {
            if (returnTriageFocus(opener.current, entry.id)) {
              event.preventDefault();
            }
          } else if (opener.current?.isConnected) {
            event.preventDefault();
            opener.current.focus();
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {mode === "accept"
              ? t("roadmap.triage.accept")
              : mode === "create"
                ? t("roadmap.form.newTitle")
                : t("roadmap.proposeIdea")}
          </DialogTitle>
          <DialogDescription>
            {entry
              ? t(`roadmap.triage.acceptDescription.${triageKind(entry)}`)
              : mode === "create"
                ? t("roadmap.form.newDescription")
                : t("roadmap.form.proposeDescription")}
          </DialogDescription>
        </DialogHeader>
        {/* Mounted while open only, so every opening starts afresh. */}
        {open &&
          (mode === "propose" ? (
            <ProposalForm onClose={close} onProposed={onProposed} />
          ) : (
            <IdeaForm
              key={entry?.id ?? "new"}
              entry={entry}
              entryGone={entryGone}
              people={people}
              onClose={close}
              onCreated={onCreated}
            />
          ))}
      </DialogContent>
    </Dialog>
  );
}

/** A developer's new idea before anything is typed: Middel, to be estimated. */
const EMPTY_IDEA: IdeaDraft = {
  title: "",
  summary: "",
  priority: "medium",
  linear: "",
  effort: null,
};

interface IdeaFormProps {
  /** The triage entry it starts from, for Naar backlog; null for Nieuw idee. */
  entry: RoadmapTriageEntry | null;
  /** The entry has left the triage meanwhile: handled elsewhere. */
  entryGone: boolean;
  /** The people the triage names its submitters among (`triagePeople`), by email in lower case. */
  people: ReadonlyMap<string, NamedPerson>;
  onClose: () => void;
  onCreated?: (item: RoadmapItem) => void;
}

/**
 * A developer's idea: title, summary, priority, Linear ids (one or more, in
 * one box, separated by commas or spaces) and an optional effort, and for
 * Naar backlog the language of the title and summary. What is wrong with the
 * Linear ids shows once the box is left with something in it, or at saving,
 * which then puts the focus in the box. An entry handled elsewhere meanwhile
 * can no longer be saved: the form says so in place of a failure, and keeps
 * what was typed.
 */
function IdeaForm({ entry, entryGone, people, onClose, onCreated }: IdeaFormProps) {
  const { t } = useTranslation();
  const ids = useId();
  const create = useCreateRoadmapItem();
  const accept = useAcceptRoadmapTriage();
  const pending = create.isPending || accept.isPending;
  // Left the triage, and not through this form's own save (which closes the dialog once the roadmap shows it).
  const handled = entryGone && (accept.isIdle || accept.isError);
  const [draft, setDraft] = useState<IdeaDraft>(() => (entry ? triageDraft(entry) : EMPTY_IDEA));
  const [checkLinear, setCheckLinear] = useState(false);
  // The failed attempt, worded at render so a language switch re-words it.
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);
  const linearBox = useRef<HTMLInputElement>(null);
  const problem = linearProblem(draft.linear);
  const shownProblem = checkLinear ? problem : null;
  const filled = draft.title.trim() !== "" && draft.summary.trim() !== "";
  const change = (edit: Partial<IdeaDraft>) => setDraft({ ...draft, ...edit });

  const submit = async () => {
    if (pending || handled) {
      return;
    }
    if (problem) {
      setCheckLinear(true);
      linearBox.current?.focus();
      return;
    }
    // Built at the moment of sending, so a new idea carries the language on show then.
    const body = ideaCreate(draft);
    if (!body) {
      return;
    }
    setFailure(null);
    try {
      const item = entry
        ? await accept.mutateAsync({ entryId: entry.id, item: body })
        : await create.mutateAsync(body);
      onCreated?.(item);
      onClose();
    } catch (error) {
      setFailure(inlineFailure({ error }));
    }
  };

  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      {entry && <EntryOrigin entry={entry} people={people} />}
      <Field label={t("roadmap.form.title")} htmlFor={`${ids}-title`}>
        <Input
          id={`${ids}-title`}
          value={draft.title}
          maxLength={TITLE_MAX}
          onChange={(event) => change({ title: event.target.value })}
          lang={draft.language}
          autoFocus
        />
      </Field>
      <Field label={t("roadmap.form.summary")} htmlFor={`${ids}-summary`}>
        <textarea
          id={`${ids}-summary`}
          value={draft.summary}
          maxLength={SUMMARY_MAX}
          rows={4}
          onChange={(event) => change({ summary: event.target.value })}
          lang={draft.language}
          className={TEXTAREA_CLASS}
        />
      </Field>
      {draft.language && (
        <Field
          label={t("roadmap.form.language")}
          htmlFor={`${ids}-language`}
          hint={t("roadmap.form.languageHint")}
        >
          <LanguageSelect
            id={`${ids}-language`}
            value={draft.language}
            onChange={(language) => change({ language })}
          />
        </Field>
      )}
      <Field label={t("roadmap.form.priority")} htmlFor={`${ids}-priority`}>
        <PrioritySelect
          id={`${ids}-priority`}
          value={draft.priority}
          onChange={(priority) => change({ priority })}
        />
      </Field>
      <div className="space-y-1.5">
        <label htmlFor={`${ids}-linear`} className="text-foreground block text-sm font-medium">
          {t("roadmap.form.linear")}
        </label>
        <Input
          ref={linearBox}
          id={`${ids}-linear`}
          value={draft.linear}
          onChange={(event) => change({ linear: event.target.value })}
          onBlur={() => {
            if (draft.linear.trim() !== "") {
              setCheckLinear(true);
            }
          }}
          placeholder={t("roadmap.form.linearPlaceholder")}
          autoCapitalize="characters"
          autoComplete="off"
          spellCheck={false}
          aria-invalid={shownProblem ? true : undefined}
          aria-describedby={`${ids}-linear-hint${shownProblem ? ` ${ids}-linear-problem` : ""}`}
        />
        <p id={`${ids}-linear-hint`} className="text-muted text-xs">
          {t("roadmap.form.linearHint")}
        </p>
        {shownProblem && (
          <p id={`${ids}-linear-problem`} role="alert" className="text-destructive text-xs">
            {linearProblemMessage(shownProblem)}
          </p>
        )}
      </div>
      <div className="space-y-1.5">
        <p className="text-foreground text-sm font-medium">{t("roadmap.form.effortOptional")}</p>
        <EffortStepper
          value={draft.effort}
          onChange={(effort) => change({ effort })}
          allowNone
          label={t("roadmap.form.effortOptional")}
        />
      </div>
      {handled ? (
        <p role="alert" className="text-destructive text-sm">
          {t("errors.codes.roadmap_triage_closed")}
        </p>
      ) : (
        failure && (
          <p role="alert" className="text-destructive text-sm">
            {apiErrorMessage(failure.error, entry ? "acceptRoadmapTriage" : "createRoadmapItem")}
          </p>
        )
      )}
      <StepButtons
        onBack={onClose}
        confirm={entry ? t("roadmap.triage.accept") : t("roadmap.form.create")}
        pending={pending}
        disabled={!filled || handled}
      />
    </form>
  );
}

interface EntryOriginProps {
  entry: RoadmapTriageEntry;
  /** The people the triage names its submitters among (`triagePeople`), by email in lower case. */
  people: ReadonlyMap<string, NamedPerson>;
}

/**
 * Above Naar backlog's form: the entry's kind, who submitted it and when,
 * named as its row does. For a question, also the question as typed and
 * Claude's note, read-only: the form starts empty, since the item is written
 * in the developer's own words (`triageDraft`).
 */
function EntryOrigin({ entry, people }: EntryOriginProps) {
  const { t } = useTranslation();
  const person = submitterLabel(entry, people);
  const kind = triageKind(entry);
  return (
    <div className="space-y-2" data-entry-origin="">
      <p className="text-muted flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
        <Chip tone="neutral">{t(`roadmap.triage.kinds.${kind}`)}</Chip>
        <span className="min-w-0 wrap-anywhere">
          {t("roadmap.triage.submittedBy", { person, date: formatDateTime(entry.created_at) })}
        </span>
      </p>
      {kind === "question" && (
        <dl className="bg-accent-light/40 space-y-1.5 rounded-md border px-3 py-2 text-sm">
          <div>
            <dt className="text-muted text-xs">{t("roadmap.triage.body.question")}</dt>
            <dd
              lang={isLanguage(entry.language) ? entry.language : undefined}
              className="text-foreground wrap-anywhere whitespace-pre-wrap"
            >
              {entry.body}
            </dd>
          </div>
          {entry.detail && (
            <div>
              <dt className="text-muted text-xs">{t("roadmap.triage.detail")}</dt>
              <dd className="text-foreground wrap-anywhere whitespace-pre-wrap">{entry.detail}</dd>
            </div>
          )}
        </dl>
      )}
    </div>
  );
}

interface ProposalFormProps {
  onClose: () => void;
  onProposed?: () => void;
}

/** Idee voorstellen: a title and a summary, both needed, and a priority if the person likes. */
function ProposalForm({ onClose, onProposed }: ProposalFormProps) {
  const { t } = useTranslation();
  const ids = useId();
  const propose = useProposeRoadmapIdea();
  const [draft, setDraft] = useState<ProposalDraft>({ title: "", summary: "", priority: null });
  // The failed attempt, worded at render so a language switch re-words it.
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);
  const filled = ideaProposal(draft) !== null;
  const change = (edit: Partial<ProposalDraft>) => setDraft({ ...draft, ...edit });

  const submit = async () => {
    // Built at the moment of sending, so it carries the language on show then.
    const body = ideaProposal(draft);
    if (!body || propose.isPending) {
      return;
    }
    setFailure(null);
    try {
      await propose.mutateAsync(body);
      onProposed?.();
      onClose();
    } catch (error) {
      setFailure(inlineFailure({ error }));
    }
  };

  return (
    <form
      className="space-y-4"
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
    >
      <Field label={t("roadmap.form.title")} htmlFor={`${ids}-title`}>
        <Input
          id={`${ids}-title`}
          value={draft.title}
          maxLength={TITLE_MAX}
          onChange={(event) => change({ title: event.target.value })}
          autoFocus
        />
      </Field>
      <Field label={t("roadmap.form.summary")} htmlFor={`${ids}-summary`}>
        <textarea
          id={`${ids}-summary`}
          value={draft.summary}
          maxLength={SUMMARY_MAX}
          rows={4}
          onChange={(event) => change({ summary: event.target.value })}
          className={TEXTAREA_CLASS}
        />
      </Field>
      <Field label={t("roadmap.form.priorityOptional")} htmlFor={`${ids}-priority`}>
        <OptionalPrioritySelect
          id={`${ids}-priority`}
          value={draft.priority}
          onChange={(priority) => change({ priority })}
        />
      </Field>
      {failure && (
        <p role="alert" className="text-destructive text-sm">
          {apiErrorMessage(failure.error, "proposeRoadmapIdea")}
        </p>
      )}
      <StepButtons
        onBack={onClose}
        confirm={t("roadmap.form.propose")}
        pending={propose.isPending}
        disabled={!filled}
      />
    </form>
  );
}
